import { useState, useEffect, useCallback } from 'react';

export const useOfflineDetection = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionType, setConnectionType] = useState('unknown');
  const [connectionSpeed, setConnectionSpeed] = useState('unknown');
  const [lastOfflineTime, setLastOfflineTime] = useState(null);
  const [offlineDuration, setOfflineDuration] = useState(0);

  // Get connection information
  const getConnectionInfo = useCallback(() => {
    if ('connection' in navigator) {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (connection) {
        return {
          type: connection.type || 'unknown',
          effectiveType: connection.effectiveType || 'unknown',
          downlink: connection.downlink || 'unknown',
          rtt: connection.rtt || 'unknown',
          saveData: connection.saveData || false
        };
      }
    }
    return {
      type: 'unknown',
      effectiveType: 'unknown',
      downlink: 'unknown',
      rtt: 'unknown',
      saveData: false
    };
  }, []);

  // Update connection information
  const updateConnectionInfo = useCallback(() => {
    const connection = getConnectionInfo();
    setConnectionType(connection.type);
    setConnectionSpeed(connection.effectiveType);
  }, [getConnectionInfo]);

  // Handle online event
  const handleOnline = useCallback(() => {
    const now = new Date();
    setIsOnline(true);
    
    // Calculate offline duration
    if (lastOfflineTime) {
      const duration = Math.floor((now - lastOfflineTime) / 1000);
      setOfflineDuration(duration);
      console.log(`[OfflineDetection] Back online after ${duration} seconds`);
    }
    
    updateConnectionInfo();
    
    // Dispatch custom event for other components
    window.dispatchEvent(new CustomEvent('app-online', {
      detail: { 
        timestamp: now,
        offlineDuration: lastOfflineTime ? Math.floor((now - lastOfflineTime) / 1000) : 0
      }
    }));
  }, [lastOfflineTime, updateConnectionInfo]);

  // Handle offline event
  const handleOffline = useCallback(() => {
    const now = new Date();
    setIsOnline(false);
    setLastOfflineTime(now);
    console.log('[OfflineDetection] Gone offline at', now.toISOString());
    
    // Dispatch custom event for other components
    window.dispatchEvent(new CustomEvent('app-offline', {
      detail: { timestamp: now }
    }));
  }, []);

  // Set up event listeners
  useEffect(() => {
    // Initial connection info
    updateConnectionInfo();

    // Online/offline event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Connection change listener (if supported)
    if ('connection' in navigator) {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (connection) {
        const handleConnectionChange = () => {
          updateConnectionInfo();
          console.log('[OfflineDetection] Connection changed:', getConnectionInfo());
        };
        
        connection.addEventListener('change', handleConnectionChange);
        
        return () => {
          window.removeEventListener('online', handleOnline);
          window.removeEventListener('offline', handleOffline);
          connection.removeEventListener('change', handleConnectionChange);
        };
      }
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [handleOnline, handleOffline, updateConnectionInfo, getConnectionInfo]);

  // Periodic connection check (fallback for some browsers)
  useEffect(() => {
    const interval = setInterval(() => {
      const currentOnline = navigator.onLine;
      if (currentOnline !== isOnline) {
        setIsOnline(currentOnline);
        if (currentOnline) {
          handleOnline();
        } else {
          handleOffline();
        }
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(interval);
  }, [isOnline, handleOnline, handleOffline]);

  return {
    isOnline,
    connectionType,
    connectionSpeed,
    lastOfflineTime,
    offlineDuration,
    connectionInfo: getConnectionInfo()
  };
};